/**
 * Last-used text label formatting, persisted per user in `localStorage`.
 * New labels are placed in it and the placement ghost previews in it, the way
 * the toolbar remembers the last shape style.
 *
 * Reads are imperative — every mounted label writes this store, and a hook
 * would re-render the whole canvas on each formatting click. Only the ghost
 * preview subscribes, and it is a leaf.
 */

import { useSyncExternalStore } from "react";
import { markOf } from "../nodes/shared/canvasColorRoles";
import {
  DEFAULT_TEXT_LABEL_FONT_SIZE,
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
  TEXT_LABEL_PLACEHOLDER_COLOR,
} from "../types/canvasNodeData";

/**
 * The prompt's ink: the chosen colour at the placeholder's own 0.55 alpha,
 * the neutral prompt colour when none is chosen (ROUND2 §4).
 */
export function labelPlaceholderColor(
  textColor: string | null | undefined,
): string {
  const ink = labelInk(textColor);
  return ink
    ? `color-mix(in oklab, ${ink} 55%, transparent)`
    : TEXT_LABEL_PLACEHOLDER_COLOR;
}

/**
 * The colour a label's text renders in: a chosen colour as the family's
 * mark (a stored dark stop resolves on read, D41), undefined for Default so
 * the theme foreground shows through.
 */
export function labelInk(
  textColor: string | null | undefined,
): string | undefined {
  return textColor && textColor !== "default" ? markOf(textColor) : undefined;
}

/** Underline and strikethrough compose on one line. */
export function labelTextDecoration(style: {
  is_underline: boolean;
  is_strikethrough: boolean;
}): string {
  const parts = [
    style.is_underline ? "underline" : null,
    style.is_strikethrough ? "line-through" : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : "none";
}

export type CanvasLabelAlign = "left" | "center" | "right";

export type CanvasLabelStyle = {
  font_size: string;
  font_family: string;
  text_color: string | null;
  is_bold: boolean;
  is_italic: boolean;
  is_underline: boolean;
  /** Content-level formats: applied to the editor, not stored on the node. */
  is_strikethrough: boolean;
  align: CanvasLabelAlign;
};

export const DEFAULT_LABEL_STYLE: CanvasLabelStyle = {
  font_size: DEFAULT_TEXT_LABEL_FONT_SIZE,
  font_family: "Inter, sans-serif",
  text_color: null,
  is_bold: false,
  is_italic: false,
  is_underline: false,
  is_strikethrough: false,
  align: "left",
};

const LABEL_STYLE_KEY = "canvas:labelStyle";

function deserialize(raw: string | null): CanvasLabelStyle {
  if (!raw) return DEFAULT_LABEL_STYLE;
  try {
    const parsed = JSON.parse(raw) as Partial<CanvasLabelStyle>;
    const align = parsed.align;
    return {
      font_size: normalizeTextLabelFontSize(parsed.font_size),
      font_family: normalizeTextLabelFontFamily(parsed.font_family),
      text_color: parsed.text_color ?? null,
      is_bold: !!parsed.is_bold,
      is_italic: !!parsed.is_italic,
      is_underline: !!parsed.is_underline,
      is_strikethrough: !!parsed.is_strikethrough,
      align:
        align === "center" || align === "right"
          ? align
          : DEFAULT_LABEL_STYLE.align,
    };
  } catch {
    return DEFAULT_LABEL_STYLE;
  }
}

let cached: CanvasLabelStyle | null = null;
const listeners = new Set<() => void>();

export function readLabelStyle(): CanvasLabelStyle {
  if (!cached) {
    try {
      cached = deserialize(window.localStorage.getItem(LABEL_STYLE_KEY));
    } catch {
      cached = DEFAULT_LABEL_STYLE;
    }
  }
  return cached;
}

export function rememberLabelStyle(patch: Partial<CanvasLabelStyle>): void {
  cached = { ...readLabelStyle(), ...patch };
  try {
    window.localStorage.setItem(LABEL_STYLE_KEY, JSON.stringify(cached));
  } catch {
    // Private-mode quota: the style still applies for this session.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== LABEL_STYLE_KEY) return;
    cached = null;
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** Leaf-only: subscribing re-renders on every formatting click. */
export function useCanvasLabelStyle(): CanvasLabelStyle {
  return useSyncExternalStore(subscribe, readLabelStyle, readLabelStyle);
}
